import { Component, effect, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { CreateContractorAvailabilityException } from '@umss/shared/data-access';
import { UmssDialogComponent } from '../../dialog/dialog.component';
import { UmssDialogFooterComponent } from '../../dialog-footer/dialog-footer.component';
import { UmssDialogHeaderComponent } from '../../dialog-header/dialog-header.component';
import { UmssTimeRangeFieldComponent } from '../../time-range-field/time-range-field.component';
import { UmssTextareaComponent } from '../../textarea/textarea.component';
import { UmssLabelComponent } from '../../label/label.component';
import { UmssButtonGroupComponent } from '../../button-group/button-group.component';
import { UmssToggleButtonComponent } from '../../toggle-button/toggle-button.component';
import { DatePipe } from '@angular/common';
import {
  getErrorsPerControl,
  TimeValidators,
  toHHmmss
} from '@umss/shared/utils';
import { debounceTime } from 'rxjs';

@Component({
  selector: 'umss-add-availability-exception-dialog',
  standalone: true,
  imports: [
    UmssDialogComponent,
    UmssDialogFooterComponent,
    UmssDialogHeaderComponent,
    UmssTimeRangeFieldComponent,
    UmssTextareaComponent,
    UmssLabelComponent,
    UmssButtonGroupComponent,
    UmssToggleButtonComponent,
    ReactiveFormsModule,
    DatePipe
  ],
  templateUrl: './add-availability-exception-dialog.component.html',
  styleUrl: './add-availability-exception-dialog.component.scss'
})
export class AddAvailabilityExceptionDialogComponent {
  open = input<boolean>(false);
  exceptionDate = input<Date | null>(null);
  defaultIsUnavailable = input<boolean>(false);

  closeDialog = output<CreateContractorAvailabilityException | undefined>();

  errors = signal<Record<string, Set<string> | null>>({});
  protected readonly isAvailable = signal(false);

  formGroup = new FormGroup({
    isUnavailable: new FormControl<boolean>(true),
    startTime: new FormControl<string | null>(null, [
      Validators.required,
      TimeValidators.isHHMM
    ]),
    endTime: new FormControl<string | null>(null, [
      Validators.required,
      TimeValidators.isHHMM
    ]),
    allDay: new FormControl<boolean>(false),
    reason: new FormControl<string | null>(null)
  });

  constructor() {
    this.onIsUnavailableChange();
    this.subscribeToCheckFormErrors();

    effect(() => {
      if (this.open()) this._resetForm();
    });
  }

  /**
   * Single source of truth for isAvailable signal and time controls state.
   * Every change to isUnavailable (user click OR programmatic setValue) flows through here.
   */
  private onIsUnavailableChange(): void {
    this.formGroup.controls.isUnavailable.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((isUnavailable) => {
        this.isAvailable.set(!isUnavailable);

        if (isUnavailable) {
          this.formGroup.controls.startTime.disable({ emitEvent: false });
          this.formGroup.controls.endTime.disable({ emitEvent: false });
          this.formGroup.controls.startTime.setValue(null, {
            emitEvent: false
          });
          this.formGroup.controls.endTime.setValue(null, { emitEvent: false });
          this.formGroup.controls.allDay.setValue(false, { emitEvent: false });
        } else {
          this.formGroup.controls.startTime.enable({ emitEvent: false });
          this.formGroup.controls.endTime.enable({ emitEvent: false });
        }
      });
  }

  private subscribeToCheckFormErrors(dTime = 500): void {
    this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(), debounceTime(dTime))
      .subscribe(() => this.checkErrors());
  }

  private checkErrors(): void {
    const errors = getErrorsPerControl(this.formGroup);
    this.errors.set(errors);
  }

  /**
   * Resets form to default state. Sets isUnavailable WITH event emission
   * so the onIsUnavailableChange subscription fires and syncs everything.
   */
  private _resetForm(): void {
    const isUnavailable = !this.defaultIsUnavailable();

    this.formGroup.controls.startTime.reset(null, { emitEvent: false });
    this.formGroup.controls.endTime.reset(null, { emitEvent: false });
    this.formGroup.controls.allDay.reset(false, { emitEvent: false });
    this.formGroup.controls.reason.reset(null, { emitEvent: false });

    // setValue WITH event emission → triggers onIsUnavailableChange → syncs isAvailable + controls
    this.formGroup.controls.isUnavailable.setValue(isUnavailable);

    this.errors.set({});
  }

  close(result?: CreateContractorAvailabilityException): void {
    this._resetForm();
    this.closeDialog.emit(result);
  }

  save(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.checkErrors();
      return;
    }

    const value = this.formGroup.getRawValue();
    const date = this.exceptionDate();
    if (!date) return;

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');

    const exception: CreateContractorAvailabilityException = {
      exceptionDate: `${y}-${m}-${d}`,
      isUnavailable: !!value.isUnavailable
    };

    if (!value.isUnavailable && value.startTime && value.endTime) {
      exception.startTime = toHHmmss(value.startTime);
      exception.endTime = toHHmmss(value.endTime);
    }

    if (value.reason) exception.reason = value.reason;

    this.close(exception);
  }
}
